import React, { useEffect, useRef, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import classNames from 'classnames'
import { addDays, addMonths, addWeeks, endOfISOWeek, format, parse, parseISO, startOfISOWeek } from 'date-fns'
import { includes, isEmpty, isInteger, isNil, isNumber, sortBy, startCase } from 'lodash-es'
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import { useWindowSize } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useWindowSize'
import { convertUTCToISO8601format } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/convertUTCToISO8601format'
import { hasCHRTHighRisk } from '@lyrahealth-inc/shared-app-logic/src/features/highAlert/utils'
import { AssessmentType } from '@lyrahealth-inc/shared-app-logic/src/models/assessment/Assessment'
import { Outcome, Scores } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'

import CustomDot from './CustomDot'
import CustomTick from './CustomTick'
import styles from './outcomeChart.module.scss'
import TextButton from '../../atoms/buttons/textButton/TextButton'
import LyraPopover, { LyraPopoverHandle } from '../../atoms/popover/LyraPopover'
import { outcomeChartConstants, outcomeChartConstantsType } from '../../constants/outcomeConstants'
import outcomeUtils from '../../utils/outcomeUtils'
import { getProductTypeColors, getSubscaleColor, hasCustomColors } from '../../utils/productTypeColors'
import { type OutcomeResponseDetailsViewData } from '../outcomeResponseDetails/OutcomeResponseDetails'

const MESSAGES = defineMessages({
  viewAnswers: {
    defaultMessage: 'View answers',
    description: 'Button that opens the answers submitted for an assessment',
  },
  viewAnswersForAssessmentOnDate: {
    defaultMessage: 'View answers for {assessment} on {date}',
    description: 'Accessibility label for opening assessment answers from a dated chart point',
  },
  viewAnswersOnDate: {
    defaultMessage: 'View answers for {date}',
    description: 'Accessibility label for opening answers from a dated chart point without an assessment label',
  },
  historyChartAccessibilityLabel: {
    defaultMessage: '{assessment} history chart',
    description: 'Accessibility label for an assessment history chart region',
  },
})

/** Backward-compatible name for the response-details payload emitted by a chart point. */
export type OutcomeChartResponse = OutcomeResponseDetailsViewData

type OutcomeChartProps = {
  outcomesData: Outcome[]
  assessmentType: string
  /** Optional in-page navigation target, applied only when this chart renders. */
  chartAnchorId?: string
  answerModalAction?: (response: OutcomeChartResponse) => void
  clientName: string
  track: (event: { event: string; action: string }) => void
  productType?: PRODUCT_TYPE
  isAuroraTheme?: boolean
  isDarkMode?: boolean
  isProviderPortal?: boolean
}

export const timelineFollowbackMethodData = (data: Outcome[]): Outcome[] => {
  const chronological = data[data.length - 1]?.response_date > data[0]?.response_date
  data = !chronological ? data.reverse() : data
  const dataValues: Record<string, any> = data.reduce(
    (entries: Record<string, any>, response) => {
      // Define start (Monday) and end (Sunday) of the previous week using date-fns
      const baseDate = parseISO(response.response_date)
      const weekStart = startOfISOWeek(addWeeks(baseDate, -1))
      const weekEnd = endOfISOWeek(addWeeks(baseDate, -1))
      for (let day = weekStart; day.getTime() <= weekEnd.getTime(); day = addDays(day, 1)) {
        const newDay = format(day, 'EEEE') // e.g., 'Monday'
        const response_date = format(day, 'yyyy-MM-dd')

        // Find score for the current day from response details
        const tFMScore = response.response_details?.find((response) => response.question === newDay)?.score

        entries[response_date] = {
          scores: {
            TFM: tFMScore && tFMScore > 7 ? 7 : tFMScore,
            'TLFB-ALCOHOL': tFMScore && tFMScore > 7 ? 7 : tFMScore,
            actualScore: tFMScore,
          },
          response_details: [response.response_details?.find((response) => response.question === newDay)],
          response_date,
        }
      }
      return entries
    },
    {} as Record<string, any>,
  )

  // Identify range of existing data
  const dataOrder = Object.keys(dataValues).sort()
  const startDate = dataOrder[0]
  const endDate = dataOrder[dataOrder.length - 1]

  // Fill missing days between start and end date with null values
  const startDateParsed = parse(startDate, 'yyyy-MM-dd', new Date())
  const endDateParsed = parse(endDate, 'yyyy-MM-dd', new Date())
  for (let date = startDateParsed; date.getTime() <= endDateParsed.getTime(); date = addDays(date, 1)) {
    const dateKey = format(date, 'yyyy-MM-dd')
    if (!(dateKey in dataValues)) {
      dataValues[dateKey] = {
        scores: {
          TFM: null,
          actualScore: null,
        },
        response_details: null,
        response_date: format(date, 'yyyy-MM-dd'),
      }
    }
  }

  // Sort final output by descending date so most recent is first
  const newResponseData: Outcome[] = sortBy(
    Object.values(dataValues) as (Outcome & { response_date: string })[],
    (entry) => parse(entry.response_date, 'yyyy-MM-dd', new Date()).getTime(),
  ).reverse()

  return newResponseData
}

export const bingeDrinkingDays = (data: Outcome[]) => {
  const chronological = data[data.length - 1]?.response_date > data[0]?.response_date
  const startDate = chronological ? data[0].response_date : data[data.length - 1].response_date
  const endDate = chronological ? data[data.length - 1].response_date : data[0].response_date
  const uniqueMonthsData: Record<string, Outcome> = sortBy(data, ['response_date']).reduce(
    (cumulative: Record<string, Outcome>, response) => {
      cumulative[format(parseISO(response.response_date), 'yyyy-MM')] = response
      return cumulative
    },
    {} as Record<string, Outcome>,
  )
  const currentMonths = Object.keys(uniqueMonthsData)
  const drinkingDaysData: Outcome[] = Object.values(uniqueMonthsData)
  {
    let date = parseISO(startDate)
    const end = parseISO(endDate)
    while (date.getTime() < end.getTime()) {
      const monthKey = format(date, 'yyyy-MM')
      if (!currentMonths.includes(monthKey)) {
        drinkingDaysData.push({
          scores: {
            BDD: null,
          },
          response_details: null,
          response_date: format(date, "yyyy-MM-dd'T'HH:mm:ss"),
        })
      }
      date = addMonths(date, 1)
    }
  }
  chronological
    ? drinkingDaysData.sort((entryA, entryB) => (entryA.response_date > entryB.response_date ? 1 : -1))
    : drinkingDaysData.sort((entryA, entryB) => (entryA.response_date < entryB.response_date ? 1 : -1))
  return drinkingDaysData
}

// Convert day lists like "['monday', 'tuesday']" → "Monday, Tuesday"
const formatDayList = (text: string): string => {
  try {
    const replaced = text.replace(/'/g, '"') // Convert single quotes to double quotes for valid JSON
    const parsed = JSON.parse(replaced) // Parse JSON string into an array
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map((day) => startCase(day)).join(', ') // Convert to "Monday, Tuesday"
    }
  } catch {
    return text
  }
  return text
}

export const timelineFollowbackMethodDataOtherSubstances = ({
  data,
  groupName,
  assessment,
  formatDate,
  windowSize,
}: {
  data: Outcome[]
  groupName: string
  assessment?: outcomeChartConstantsType
  formatDate?: any
  windowSize?: number[]
}): (Outcome & { title?: string; dateRange?: string })[] => {
  const formatAnswerText = (text: string): string => startCase(text) // Convert camelCase to Title Case

  // Sort responses by submission date in descending order (newest first)
  const sortedData = [...data].sort((a, b) => new Date(b.response_date).getTime() - new Date(a.response_date).getTime())

  // Map to store the latest entry for each (answer-text + cutoff-week) combination
  const latestEntries = new Map<string, Outcome & { title?: string; dateRange?: string }>()

  /*
    ┌───────────────┬──────────────────────────────┬───────────────────────────┬───────────────────────┐
    │ Submission    │ Previous Sunday (Cutoff)     │ Full Week Range           │ Answer-Text           │
    ├───────────────┼──────────────────────────────┼───────────────────────────┼───────────────────────┤
    │ 3/02/25 (Sun) │ 2/23/25                      │ 2/23/25 - 3/1/25          │ Tobacco Products      │
    │ 3/06/25 (Thu) │ 2/23/25                      │ 2/23/25 - 3/1/25          │ Tobacco Products      │ (Overwrites 3/2)
    │ 3/13/25 (Thu) │ 3/02/25                      │ 3/2/25 - 3/8/25           │ Other Substance Use   │
    │ 3/18/25 (Tue) │ 3/09/25                      │ 3/9/25 - 3/15/25          │ Addictive Behavior    │
    │ 3/23/25 (Sun) │ 3/16/25                      │ 3/16/25 - 3/22/25         │ Marijuana Use         │
    │ 3/29/25 (Sat) │ 3/23/25                      │ 3/23/25 - 3/29/25         │ Caffeine Consumption  │
    │ 4/02/25 (Wed) │ 3/30/25                      │ 3/30/25 - 4/5/25          │ Tobacco Products      │
    │ 4/08/25 (Tue) │ 4/06/25                      │ 4/6/25 - 4/12/25          │ Other Substance Use   │
    └───────────────┴──────────────────────────────┴───────────────────────────┴───────────────────────┘

    //
    // **Grouping Logic:**
    // 1) **Previous Sunday (Cutoff Date)** → Groups responses into **weekly periods** based on the previous Sunday.
    // 2) **Substance Type (Main Question Answer)** → Separates responses **within the same week** based on the type of substance tracked.
    // 3) **Most Recent Entry Wins** → If there are multiple entries for the same cutoff week and same substance type,
    //    only the most recent one (by submission date) is kept.
    //
    // **Edge Case Consideration:**
    // - Typically, users do **not** change their selected substance type from one assessment to another within the same week.
    // - However, if multiple entries exist for the same cutoff week with **different substance types**, they will be kept as separate entries.
    // - This is considered an **edge case** but is handled to ensure accurate tracking.
    */

  sortedData.forEach((outcome) => {
    const responseDetails = outcome.response_details ?? []

    // Extract all TLFB-related details
    let tlfbDetails = responseDetails.filter((detail) => detail.groups.includes(groupName))

    // Locate the main question used to categorize the type of substance or behavior
    // This question is required on the client side, so it should always be present in response_details
    const mainQuestionDetail = tlfbDetails.find((detail) =>
      detail.question?.toLowerCase().includes('select the main type of substance use or behavior'),
    )

    // Format answer-text: convert day lists into readable format and other text to Title Case
    tlfbDetails = tlfbDetails.map((detail) => ({
      ...detail,
      'answer-text': detail['answer-text']?.includes('[')
        ? formatDayList(detail['answer-text']) // Convert day lists into "Monday, Tuesday"
        : formatAnswerText(detail['answer-text'] ?? ''), // Convert text to Title Case
    }))

    // Compute previous Sunday (UTC) for week grouping, then the end of that week (Saturday)
    const responseDate = new Date(outcome.response_date)
    const utcBase = new Date(
      Date.UTC(responseDate.getUTCFullYear(), responseDate.getUTCMonth(), responseDate.getUTCDate()),
    )
    const dayOfWeek = utcBase.getUTCDay() // 0 = Sunday
    const previousSunday = addDays(utcBase, -dayOfWeek - 7)
    const endOfWeek = addDays(previousSunday, 6)

    // Format the response date shown on the bottom X-axis of the bar graph
    // Uses the previous Sunday and screen size to determine formatting (adds year on desktop)
    const formattedResponseDate =
      formatDate && windowSize
        ? formatDate(previousSunday, {
            day: 'numeric',
            month: 'numeric',
            ...(windowSize[0] > 768 && { year: '2-digit' }),
            timeZone: 'UTC',
          })
        : format(previousSunday, 'yyyy-MM-dd')

    // Full date range to display for the week (e.g., 9/8/19 - 9/14/19) shown in tooltip and modal
    const dateRange = formatDate
      ? `${formatDate(previousSunday, {
          ...assessment?.toolTipFormat,
          timeZone: 'UTC',
        })}-${formatDate(endOfWeek, {
          ...assessment?.toolTipFormat,
          timeZone: 'UTC',
        })}`
      : `${format(previousSunday, 'yyyy-MM-dd')} - ${format(endOfWeek, 'yyyy-MM-dd')}`

    const rawScore = (outcome.scores as Record<string, number | null>)[groupName]

    const scores: Scores = {
      [groupName]: rawScore,
      actualScore: rawScore,
    } as Scores

    const formattedEntry: Outcome & { title?: string; dateRange?: string } = {
      scores,
      response_details: tlfbDetails,
      response_date: formattedResponseDate,
      title: formatAnswerText(mainQuestionDetail?.['answer-text'] ?? ''),
      dateRange,
    }

    const key = `${formattedResponseDate}||${formattedEntry.title}` // separate groups by both week and substance type

    // Always keep the latest (since data is sorted desc), overwrite if already exists
    latestEntries.set(key, formattedEntry)
  })

  // Group by week to determine how to handle title visibility
  const weekGroups = new Map<string, (Outcome & { title?: string; dateRange?: string })[]>()
  for (const [key, entry] of latestEntries.entries()) {
    const [week] = key.split('||')
    if (!weekGroups.has(week)) weekGroups.set(week, [])
    weekGroups.get(week)!.push(entry)
  }

  const finalEntries: (Outcome & { title?: string; dateRange?: string })[] = []
  weekGroups.forEach((entries) => {
    const uniqueTitles = new Set(entries.map((e) => e.title))
    if (entries.length === 1 || uniqueTitles.size === 1) {
      // Only one distinct dataset – remove title to avoid unnecessary tooltip clutter
      entries.forEach(({ title: _unusedTitle, ...entryWithoutTitle }) => {
        finalEntries.push(entryWithoutTitle)
      })
    } else {
      // Multiple distinct datasets – keep titles for clarity in tooltip
      finalEntries.push(...entries)
    }
  })

  return finalEntries
}

export const OutcomeChart: React.FC<OutcomeChartProps> = ({
  answerModalAction,
  assessmentType,
  chartAnchorId,
  clientName,
  outcomesData,
  track,
  productType = PRODUCT_TYPE.WFMH,
  isAuroraTheme,
  isDarkMode,
  isProviderPortal,
}) => {
  const assessmentKey = assessmentType as keyof Scores
  const [toolTipActive, setToolTipActive] = useState(false)
  const [activeX, setActiveX] = useState(0)
  const [activeY, setActiveY] = useState(0)
  const [toolTipContent, setToolTipContent] = useState<(Outcome & { title: string }) | object>({})
  const [overTooltip, setOverTooltip] = useState(false)
  const [tooltipIndex, setTooltipIndex] = useState(-1)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [isAlreadyConvertedToUTC, setIsAlreadyConvertedToUTC] = useState(false)
  const [activeSubscaleKey, setActiveSubscaleKey] = useState<string | null>(null)
  const windowSize = useWindowSize()
  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const customYaxisRef = useRef<HTMLDivElement | null>(null)
  // Note: in recharts 2.x, refs on chart components are forward-refed and don't reliably
  // expose `.props.points` like in 1.x. We keep the refs typed as `any` so the existing
  // tooltip-positioning logic compiles; the optional-chaining on `?.props?.points` will
  // safely no-op if the shape isn't there at runtime.
  const lineRef = useRef<any>(null)
  const subscaleLineRefs = useRef<any[]>([])
  const popoverRef = useRef<LyraPopoverHandle>(null)
  const { formatDate, formatMessage } = useIntl()

  const [yaxisWidth, setYaxisWidth] = useState(50)

  const chartHeight = 440
  const minDataPointWidth = 75

  const assessment = outcomeChartConstants.find((obj) => obj.name === assessmentType)
  const isMobile = window.innerWidth < 768
  const XAxisPadding = isMobile ? { left: 70, right: 45 } : { left: 115, right: 60 }
  const ranges = assessment?.ranges

  // Get colors based on product type
  const productColors = getProductTypeColors(productType, isAuroraTheme)
  const useCustomColors = hasCustomColors(productType, isAuroraTheme)
  const chartLineColor = useCustomColors ? productColors.primaryAction : assessment?.lineColor

  useEffect(() => {
    // Set initial scroll position on load
    const chartContainer = chartContainerRef?.current
    if (chartContainer && windowSize) {
      setScrollPosition(chartContainer.scrollWidth - chartContainer.clientWidth)
    }
  }, [windowSize])

  useEffect(() => {
    if (customYaxisRef.current) {
      setYaxisWidth(customYaxisRef.current.offsetWidth)
    }
  }, [windowSize])

  useEffect(() => {
    // For mobile: Close tooltip when touch outside
    const hideTooltip = (event: MouseEvent) => {
      if (
        toolTipActive &&
        chartContainerRef.current &&
        !chartContainerRef.current.contains(event.target as HTMLElement)
      ) {
        setToolTipActive(false)
        setTooltipIndex(-1)
      }
    }
    // @ts-expect-error TS(2769): No overload matches this call.
    document.addEventListener('touchstart', hideTooltip)
    return () => {
      // @ts-expect-error TS(2769): No overload matches this call.
      document.removeEventListener('touchstart', hideTooltip)
    }
  }, [toolTipActive])

  const onChartMouseMove = (chart: any) => {
    popoverRef?.current?.forceClose()
    if (!chart.isTooltipActive) return

    if (assessment?.subscales) {
      setToolTipActive(true)
      const cursorY = chart.activeCoordinate?.y
      let closestIndex = 0
      let minDist = Infinity
      subscaleLineRefs.current.forEach((ref, i) => {
        const point = ref?.props?.points?.[chart.activeTooltipIndex]
        if (point && cursorY !== undefined) {
          const dist = Math.abs(point.y - cursorY)
          if (dist < minDist) {
            minDist = dist
            closestIndex = i
          }
        }
      })
      const closestRef = subscaleLineRefs.current[closestIndex]
      const activePoint: any = {
        payload: {},
        ...(closestRef?.props?.points ? closestRef.props.points[chart?.activeTooltipIndex] : undefined),
      }
      if (activePoint) {
        setActiveX(activePoint.x ?? 0)
        setActiveY(activePoint.y ?? 0)
        setToolTipContent(activePoint?.payload)
        setTooltipIndex(chart.activeTooltipIndex)
        setActiveSubscaleKey(assessment.subscales[closestIndex].key)
      }
    } else if (tooltipIndex !== chart.activeTooltipIndex) {
      setToolTipActive(true)
      const activePoint: any = {
        payload: {},
        ...(lineRef?.current?.props?.points ? lineRef.current.props.points[chart?.activeTooltipIndex] : undefined),
      }
      if (activePoint) {
        setActiveX(activePoint.x ?? 0)
        setActiveY(activePoint.y ?? 0)
        setToolTipContent(activePoint?.payload)
        setTooltipIndex(chart.activeTooltipIndex)
      }
    }
  }

  const onChartMouseLeave = () => {
    setTimeout(() => {
      if (!overTooltip) {
        setToolTipActive(false)
        setTooltipIndex(-1)
        setActiveSubscaleKey(null)
        // Mainly for Bar graphs
        setIsAlreadyConvertedToUTC(false)
      }
    }, 3000) // To prevent tooltip flicker
  }

  const onPopoverOpen = () => {
    track({ event: 'BUTTON_PRESS', action: `OUTCOMES_${assessmentType}_INFO` })
    setToolTipActive(false)
    setTooltipIndex(-1)
  }

  const getFormattedDateToShow = (): string => {
    const outcome = toolTipContent as Outcome & { title?: string; dateRange?: string }

    // If the date has already been preprocessed(bar chart does), return the precomputed dateRange
    if (isAlreadyConvertedToUTC && outcome.dateRange) {
      return outcome.dateRange // e.g., "9/8/19 - 9/14/19"
    }

    // Otherwise, fallback to formatting the raw UTC response_date into a localized short date
    return formatDate(convertUTCToISO8601format(outcome.response_date), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const handleAnswersClick = () => {
    track({ event: 'BUTTON_PRESS', action: `OUTCOMES_${assessmentType}_TOOLTIP` })
    if (!('response_date' in toolTipContent)) return
    const answers = toolTipContent.response_details
    const assessment = outcomeChartConstants.find((obj) => obj.name === assessmentType)
    const dateToShow = getFormattedDateToShow()
    const dateStarted =
      (toolTipContent.date_created &&
        formatDate(convertUTCToISO8601format(toolTipContent.date_created), {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })) ??
      ''

    const score = toolTipContent.scores[assessmentKey]

    const data = {
      clientName: clientName,
      title: assessment?.fullName,
      dateSubmitted: dateToShow,
      dateStarted: dateStarted,
      assessmentType: assessmentType,
      score,
      answers: answers?.filter((a) => includes(a.groups, assessment?.responseDetailsKey ?? assessmentType)),
    }
    answerModalAction && answerModalAction(data)
  }

  const renderCustomTooltip = () => {
    if (!('response_date' in toolTipContent)) return <></>
    const toolTipWidth = assessment?.subscales ? 180 : 140
    const title = toolTipContent.title
    let score = toolTipContent.scores[assessmentKey]
    let selfHarm = false

    if (assessmentType === AssessmentType.TFM || assessmentType === AssessmentType.TLFB_ALCOHOL) {
      score = toolTipContent.scores.actualScore
    }

    const scoreDescription = outcomeUtils
      .convertOutcomeScoreToDescription(assessmentType, score)
      .replace(assessment?.condition || '', '')

    if (
      assessmentType === 'PHQ' &&
      'PHQ-SELF-HARM' in toolTipContent.scores &&
      toolTipContent.scores['PHQ-SELF-HARM'] &&
      toolTipContent.scores['PHQ-SELF-HARM'] > 0
    ) {
      selfHarm = true
    }

    const chrtRisk = assessmentType.includes(AssessmentType.CHRT) && hasCHRTHighRisk(toolTipContent.response_details)

    const dateToShow = getFormattedDateToShow()

    return (
      <div
        data-test-id='OutcomeChart-customTooltip'
        onMouseOver={() => setOverTooltip(true)}
        onFocus={() => setOverTooltip(true)}
        onMouseOut={() => setOverTooltip(false)}
        onBlur={() => setOverTooltip(false)}
        className={styles['tooltip-container']}
        style={{
          left: activeX - toolTipWidth / 2 - scrollPosition,
          bottom: chartHeight - activeY + 30,
          width: toolTipWidth,
        }}
      >
        <div className={styles['tooltip-inner']}>
          {assessment?.subscales ? (
            <>
              {assessment.subscales.map((subscale) => {
                const isActive = subscale.key === activeSubscaleKey
                const subscaleColor = getSubscaleColor(subscale.colors, { isProviderPortal, isDarkMode })
                return (
                  <div
                    key={subscale.key}
                    className={`${styles['subscale-tooltip-row']} ${
                      isActive ? styles['subscale-tooltip-row--active'] : ''
                    }`}
                  >
                    <span className={styles['subscale-label']} style={{ color: subscaleColor }}>
                      {subscale.label}
                    </span>
                    <span className={styles['subscale-score']} style={{ color: subscaleColor }}>
                      {toolTipContent.scores[subscale.key as keyof Scores] ?? '—'}
                    </span>
                  </div>
                )
              })}
              <div className={styles['score-date']}>{getFormattedDateToShow()}</div>
            </>
          ) : assessmentType === 'TLFB-OTHER-SUBSTANCES' ? (
            <div
              dir='ltr'
              className={title ? styles['labeled-score-number'] : styles['score-number']}
              style={{ color: useCustomColors ? productColors.primaryAction : undefined }}
            >
              <div>{title}</div>
              {`${score} ${score === 1 ? 'day' : 'days'} of use`}
            </div>
          ) : (
            <div
              className={styles['score-number']}
              style={{ color: useCustomColors ? productColors.primaryAction : undefined }}
            >
              {score}
            </div>
          )}
          {!assessment?.subscales && (
            <>
              <div className={styles['score-description']}>{scoreDescription}</div>
              <div className={styles['score-date']}>{dateToShow}</div>
              {selfHarm ? <p className={styles['self-harm']}>Thoughts of self harm or suicide</p> : ''}
              {chrtRisk ? (
                <p data-test-id='OutcomeChart-chrtRisk' className={styles['self-harm']}>
                  CHRT risk
                </p>
              ) : (
                ''
              )}
              {answerModalAction && toolTipContent.response_details && (
                <TextButton
                  text={formatMessage(MESSAGES.viewAnswers)}
                  data-test-id='OutcomeChart-viewAnswers'
                  onClick={handleAnswersClick}
                  aria-label={formatMessage(
                    title ? MESSAGES.viewAnswersForAssessmentOnDate : MESSAGES.viewAnswersOnDate,
                    title ? { assessment: title, date: dateToShow } : { date: dateToShow },
                  )}
                  isSmall
                  isPrimary
                  productType={productType}
                  isAuroraTheme={isAuroraTheme}
                />
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const renderCustomYaxisLabels = () => {
    return (
      <div
        ref={customYaxisRef}
        className={classNames(styles['custom-yaxis'], {
          [styles.mobile]: isMobile,
          [styles.aurora]: isAuroraTheme,
        })}
      >
        {ranges?.map(({ upper, lower, description }, index) => {
          const totalRange = ranges[ranges.length - 1].upper
          const isLast = index === ranges?.length - 1
          const labelOfTypeRange = !isNil(lower)
          const lowerValue = labelOfTypeRange ? lower || 0 : assessment?.ticks[index] || 0
          const upperValue = labelOfTypeRange && !isLast ? upper + 1 : upper
          const rangeSize = upperValue - lowerValue
          const rangePercentage = (rangeSize / totalRange) * 100
          const showZeroLabel = !labelOfTypeRange && index === 0
          return (
            <div
              key={description}
              className={classNames(
                styles['yaxis-label'],
                !labelOfTypeRange ? styles.point : '',
                showZeroLabel ? styles.zero : '',
              )}
              style={{ flexBasis: `${rangePercentage}%` }}
            >
              <span>{description.replace(assessment?.condition ?? '', '')}</span>
              {showZeroLabel && <span>0</span>}
            </div>
          )
        })}
      </div>
    )
  }

  // Helper function to sort outcomes by response_date (newest first for chart's reversed XAxis)
  const sortOutcomesByDate = (data: Outcome[]): Outcome[] => {
    return data.sort((a, b) => new Date(b.response_date).getTime() - new Date(a.response_date).getTime())
  }

  let data: Outcome[] = outcomesData.filter((outcome) => isInteger(outcome.scores[assessmentKey]))
  if (assessmentType === AssessmentType.TFM && data) data = timelineFollowbackMethodData(data)
  else if (assessmentType === AssessmentType.TLFB_ALCOHOL) data = timelineFollowbackMethodData(data)
  else if (assessmentType === AssessmentType.TLFB_OTHER_SUBSTANCES)
    data = timelineFollowbackMethodDataOtherSubstances({
      data,
      groupName: AssessmentType.TLFB_OTHER_SUBSTANCES,
      assessment,
      formatDate,
      windowSize,
    })
  else if (assessmentType === AssessmentType.BDD && data.length > 0) data = bingeDrinkingDays(data)
  else if (assessmentType === AssessmentType.CGSQ || assessmentType === AssessmentType.PARENTING_TASK) {
    data = outcomesData.filter((outcome) => isNumber(outcome.scores[assessmentKey]))
    data = sortOutcomesByDate(data)
  } else if (assessment?.subscales?.length) {
    data = outcomesData.filter((outcome) =>
      assessment?.subscales?.some((s) => isNumber(outcome.scores[s.key as keyof Scores])),
    )
    data = sortOutcomesByDate(data)
  } else {
    data = sortOutcomesByDate(data)
  }
  if (isEmpty(data)) return null
  const xAxisTicks = Array.from(new Set([data[data.length - 1].response_date, data[0].response_date]))
  const barGraphAssessmentTypes = [AssessmentType.TLFB_OTHER_SUBSTANCES]
  return (
    <div
      id={chartAnchorId}
      aria-label={
        chartAnchorId
          ? formatMessage(MESSAGES.historyChartAccessibilityLabel, {
              assessment: assessment?.title ?? assessmentType,
            })
          : undefined
      }
      role={chartAnchorId ? 'region' : undefined}
      tabIndex={chartAnchorId ? -1 : undefined}
      data-test-id={`OutcomeChart-container-${assessment?.title}`}
      className={classNames({ [`product-type-${productType?.toLowerCase()}`]: useCustomColors })}
    >
      <div className={styles.title} style={{ color: isAuroraTheme ? productColors.textSecondary : undefined }}>
        {assessment?.title}
        <LyraPopover
          onOpen={onPopoverOpen}
          content={<p>{assessment?.description}</p>}
          iconId='assessment-description'
          iconSize={16}
          openOnHover
          ref={popoverRef}
          productType={productType}
          isAuroraTheme={isAuroraTheme}
        />
      </div>
      {assessment?.subscales && (
        <div className={styles['chart-legend']}>
          {assessment.subscales.map((subscale) => (
            <div key={subscale.key} className={styles['legend-item']}>
              <div
                className={styles['legend-swatch']}
                style={{ backgroundColor: getSubscaleColor(subscale.colors, { isProviderPortal, isDarkMode }) }}
              />
              {subscale.label}
            </div>
          ))}
        </div>
      )}
      <div data-test-id={`OutcomeChart-chart-${assessmentType}`} className={classNames(styles.container)}>
        {renderCustomYaxisLabels()}
        <div
          className={classNames(styles['chart-container'])}
          ref={chartContainerRef}
          onScroll={({ currentTarget: { scrollLeft, scrollWidth, clientWidth } }) => {
            setScrollPosition(scrollWidth - clientWidth + scrollLeft)
          }}
        >
          <div style={{ minWidth: data.length * minDataPointWidth }}>
            <ResponsiveContainer height={chartHeight} width='100%'>
              {!barGraphAssessmentTypes.includes(assessmentType as AssessmentType) ? (
                <LineChart
                  data={data}
                  margin={{ top: 5, right: 5, bottom: 25, left: yaxisWidth }}
                  onClick={() => {}}
                  onMouseMove={onChartMouseMove}
                  onMouseLeave={onChartMouseLeave}
                >
                  <CartesianGrid vertical={false} stroke={isAuroraTheme ? productColors.dividerPrimary : undefined} />
                  {assessment?.subscales ? (
                    assessment.subscales.map((subscale, index) => (
                      <Line
                        key={subscale.key}
                        ref={(el: any) => {
                          subscaleLineRefs.current[index] = el
                        }}
                        type='linear'
                        isAnimationActive={false}
                        dataKey={`scores.${subscale.key}`}
                        stroke={getSubscaleColor(subscale.colors, { isProviderPortal, isDarkMode })}
                        strokeWidth={2}
                        dot={
                          <CustomDot
                            tooltipIndex={tooltipIndex}
                            fillNoValueEntries={assessment.fillNoValueEntries}
                            productType={productType}
                            isAuroraTheme={isAuroraTheme}
                          />
                        }
                        activeDot={
                          <CustomDot
                            isActive
                            tooltipIndex={tooltipIndex}
                            fillNoValueEntries={assessment.fillNoValueEntries}
                            productType={productType}
                            isAuroraTheme={isAuroraTheme}
                          />
                        }
                      />
                    ))
                  ) : (
                    <Line
                      ref={lineRef}
                      type='linear'
                      isAnimationActive={false}
                      dataKey={`scores.${assessmentType}`}
                      stroke={chartLineColor}
                      strokeWidth={2}
                      dot={
                        <CustomDot
                          tooltipIndex={tooltipIndex}
                          fillNoValueEntries={assessment?.fillNoValueEntries}
                          productType={productType}
                          isAuroraTheme={isAuroraTheme}
                        />
                      }
                      activeDot={
                        <CustomDot
                          isActive
                          tooltipIndex={tooltipIndex}
                          fillNoValueEntries={assessment?.fillNoValueEntries}
                          productType={productType}
                          isAuroraTheme={isAuroraTheme}
                        />
                      }
                    />
                  )}
                  <XAxis
                    reversed
                    dataKey='response_date'
                    axisLine={false}
                    tickLine={false}
                    tick={<CustomTick data={data} {...(isAuroraTheme && { fillColor: productColors.textSecondary })} />}
                    ticks={xAxisTicks}
                    tickCount={2}
                    padding={XAxisPadding}
                  />
                  <YAxis
                    dataKey={`scores.${assessmentType}`}
                    axisLine={false}
                    tickLine={false}
                    width={0}
                    domain={[
                      ranges ? (ranges[0]?.lower ?? 0) : 0,
                      ranges ? (ranges[ranges.length - 1]?.upper ?? ranges[ranges.length - 1].lower ?? 0) : 0,
                    ]}
                    // recharts 2.x's `tick` typings restrict this to SVG text props;
                    // `marginBottom` was always invalid for SVG text but recharts 1.x
                    // accepted it. The YAxis has width={0} so labels are hidden anyway,
                    // so this had no visible effect — cast to preserve prior behavior.
                    tick={{ marginBottom: 50 } as any}
                    ticks={assessment?.ticks}
                  />
                  <Tooltip wrapperStyle={{ display: 'none' }} />
                </LineChart>
              ) : (
                <BarChart margin={{ top: 5, right: 5, bottom: 25, left: yaxisWidth }} data={data}>
                  <CartesianGrid vertical={false} stroke={isAuroraTheme ? productColors.dividerPrimary : undefined} />
                  <Bar
                    dataKey={`scores.${assessmentType}`}
                    fill={chartLineColor}
                    maxBarSize={215} // Max column width of individual bars
                    radius={[10, 10, 0, 0]} // Corner radius of individual bars
                    onMouseEnter={(barData) => {
                      // barData.x represents the X position of the column in the graph
                      // barData.width is the width of the column
                      // We divide barData.width by 2 to center the tooltip in the column
                      setActiveX(barData.x + barData.width / 2)
                      setActiveY(barData.y)
                      setToolTipContent(barData.payload) // Store tooltip data from the hovered column
                      setToolTipActive(true)

                      // This marks that the date was already pre-formatted as UTC in `timelineFollowbackMethodDataOtherSubstances`
                      // So we skip any further conversion in the tooltip display logic
                      setIsAlreadyConvertedToUTC(true)
                    }}
                    onMouseLeave={onChartMouseLeave}
                  />
                  <XAxis reversed dataKey='response_date' />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    domain={[
                      ranges ? (ranges[0]?.lower ?? 0) : 0,
                      ranges ? (ranges[ranges.length - 1]?.upper ?? ranges[ranges.length - 1].lower ?? 0) : 0,
                    ]}
                    tick={{ dx: -40 }}
                    ticks={assessment?.ticks}
                  />
                  <Tooltip wrapperStyle={{ display: 'none' }} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
          {toolTipActive && renderCustomTooltip()}
        </div>
      </div>
    </div>
  )
}
